import * as THREE from 'three';
import { pointInPolygon } from './floorPlanEditorGeometry';

export const MIN_WALL_CLEARANCE = 0.3;
export const CAMERA_WALL_CLEARANCE = 0.35;
export const CAMERA_WALL_MARGIN_M = CAMERA_WALL_CLEARANCE;
export const CAMERA_MOVE_STEP_M = 0.5;
export const CAMERA_MOVE_SPEED = 1.2;
export const MAX_CAMERA_MOVE_SPEED = CAMERA_MOVE_SPEED;
export const JOYSTICK_DEAD_ZONE = 0.08;
export const CAMERA_HEIGHT = 1.55;
export const TARGET_HEIGHT = 1.45;

const ANGULAR_SAMPLES_DEG = [0, -15, 15, -30, 30];

/**
 * @param {number[][]} points
 * @returns {{ x: number; y: number }}
 */
export function polygonCenter(points) {
  if (!Array.isArray(points) || points.length === 0) return { x: 0, y: 0 };
  const sum = points.reduce(
    (acc, point) => ({
      x: acc.x + Number(point?.[0] || 0),
      y: acc.y + Number(point?.[1] || 0),
    }),
    { x: 0, y: 0 },
  );
  return { x: sum.x / points.length, y: sum.y / points.length };
}

/**
 * @param {number[][]} points
 * @returns {number}
 */
function polygonSignedArea(points) {
  if (!Array.isArray(points) || points.length < 3) return 0;
  let area = 0;
  for (let i = 0; i < points.length; i += 1) {
    const j = (i + 1) % points.length;
    area += points[i][0] * points[j][1] - points[j][0] * points[i][1];
  }
  return area / 2;
}

/**
 * @param {number[][]} polygon
 * @returns {{ widthM: number; depthM: number; minDimensionM: number }}
 */
function polygonDimensionsM(polygon, metersPerPixel) {
  const xs = polygon.map((point) => point[0]);
  const ys = polygon.map((point) => point[1]);
  const widthM = (Math.max(...xs) - Math.min(...xs)) * metersPerPixel;
  const depthM = (Math.max(...ys) - Math.min(...ys)) * metersPerPixel;
  return { widthM, depthM, minDimensionM: Math.min(widthM, depthM) };
}

/**
 * @param {number} worldX
 * @param {number} worldZ
 * @param {{ x: number; y: number }} center
 * @param {number} metersPerPixel
 * @returns {[number, number]}
 */
export function worldToPolygonPoint(worldX, worldZ, center, metersPerPixel) {
  if (!Number.isFinite(metersPerPixel) || metersPerPixel <= 0) return [0, 0];
  return [center.x + worldX / metersPerPixel, center.y + worldZ / metersPerPixel];
}

/**
 * @param {[number, number]} polygonPoint
 * @param {{ x: number; y: number }} center
 * @param {number} metersPerPixel
 * @returns {{ x: number; z: number }}
 */
export function polygonPointToWorld(polygonPoint, center, metersPerPixel) {
  return {
    x: (polygonPoint[0] - center.x) * metersPerPixel,
    z: (polygonPoint[1] - center.y) * metersPerPixel,
  };
}

/**
 * @param {number} px
 * @param {number} py
 * @param {number} x1
 * @param {number} y1
 * @param {number} x2
 * @param {number} y2
 * @returns {number}
 */
function pointToSegmentDistancePx(px, py, x1, y1, x2, y2) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(px - x1, py - y1);
  let t = ((px - x1) * dx + (py - y1) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  const projX = x1 + t * dx;
  const projY = y1 + t * dy;
  return Math.hypot(px - projX, py - projY);
}

/**
 * @param {[number, number]} point
 * @param {number[][]} polygon
 * @param {number} metersPerPixel
 * @returns {number} distance in meters
 */
export function distancePointToPolygonEdgeM(point, polygon, metersPerPixel) {
  if (!Array.isArray(polygon) || polygon.length < 2 || !Number.isFinite(metersPerPixel)) {
    return Infinity;
  }
  let minDistPx = Infinity;
  for (let i = 0; i < polygon.length; i += 1) {
    const j = (i + 1) % polygon.length;
    const dist = pointToSegmentDistancePx(
      point[0],
      point[1],
      polygon[i][0],
      polygon[i][1],
      polygon[j][0],
      polygon[j][1],
    );
    if (dist < minDistPx) minDistPx = dist;
  }
  return minDistPx * metersPerPixel;
}

/**
 * @param {{ x: number; y: number; z: number }} worldPos
 * @param {number[][]} roomPolygon
 * @param {number} metersPerPixel
 * @param {number} [marginM]
 * @returns {boolean}
 */
export function isValidCameraWorldPosition(worldPos, roomPolygon, metersPerPixel, marginM = CAMERA_WALL_MARGIN_M) {
  if (
    !worldPos ||
    !Array.isArray(roomPolygon) ||
    roomPolygon.length < 3 ||
    !Number.isFinite(metersPerPixel)
  ) {
    return false;
  }
  const center = polygonCenter(roomPolygon);
  const polygonPoint = worldToPolygonPoint(worldPos.x, worldPos.z, center, metersPerPixel);
  if (!pointInPolygon(roomPolygon, polygonPoint)) return false;
  const edgeDistM = distancePointToPolygonEdgeM(polygonPoint, roomPolygon, metersPerPixel);
  return edgeDistM >= marginM;
}

/**
 * @param {number} x
 * @param {number} y
 * @returns {{ x: number; y: number }}
 */
function normalize2d(x, y) {
  const len = Math.hypot(x, y);
  if (len < 1e-8) return { x: 0, y: 0 };
  return { x: x / len, y: y / len };
}

/**
 * @param {number} x
 * @param {number} y
 * @param {number} angleRad
 * @returns {{ x: number; y: number }}
 */
function rotate2d(x, y, angleRad) {
  const cos = Math.cos(angleRad);
  const sin = Math.sin(angleRad);
  return { x: x * cos - y * sin, y: x * sin + y * cos };
}

/**
 * @param {[number, number]} point
 * @param {number[][]} polygon
 * @param {number} metersPerPixel
 * @param {number} marginM
 * @returns {boolean}
 */
function isValidInteriorPolygonPoint(point, polygon, metersPerPixel, marginM) {
  if (!pointInPolygon(polygon, point)) return false;
  return distancePointToPolygonEdgeM(point, polygon, metersPerPixel) >= marginM;
}

/**
 * Grid-sample the polygon bbox and return the interior point with maximum wall clearance.
 * @param {number[][]} polygon
 * @param {number} metersPerPixel
 * @param {number} [marginM]
 * @param {number} [gridSize]
 * @returns {[number, number] | null}
 */
export function findBestInteriorPoint(polygon, metersPerPixel, marginM = CAMERA_WALL_MARGIN_M, gridSize = 20) {
  if (!Array.isArray(polygon) || polygon.length < 3 || !Number.isFinite(metersPerPixel)) {
    return null;
  }
  const xs = polygon.map((point) => point[0]);
  const ys = polygon.map((point) => point[1]);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  let bestPoint = null;
  let bestClearance = -1;

  for (let gi = 0; gi <= gridSize; gi += 1) {
    for (let gj = 0; gj <= gridSize; gj += 1) {
      const x = minX + ((maxX - minX) * gi) / gridSize;
      const y = minY + ((maxY - minY) * gj) / gridSize;
      const point = [x, y];
      if (!isValidInteriorPolygonPoint(point, polygon, metersPerPixel, marginM)) continue;
      const clearance = distancePointToPolygonEdgeM(point, polygon, metersPerPixel);
      if (clearance > bestClearance) {
        bestClearance = clearance;
        bestPoint = point;
      }
    }
  }

  return bestPoint;
}

/**
 * @param {[number, number]} vertex
 * @param {[number, number]} prev
 * @param {[number, number]} next
 * @returns {{ x: number; y: number }}
 */
function computeInteriorBisector(vertex, prev, next, polygon) {
  const toPrev = normalize2d(prev[0] - vertex[0], prev[1] - vertex[1]);
  const toNext = normalize2d(next[0] - vertex[0], next[1] - vertex[1]);
  let bx = toPrev.x + toNext.x;
  let by = toPrev.y + toNext.y;
  const len = Math.hypot(bx, by);
  if (len < 1e-8) {
    bx = -toPrev.y;
    by = toPrev.x;
  } else {
    bx /= len;
    by /= len;
  }

  const testPoint = [vertex[0] + bx * 0.5, vertex[1] + by * 0.5];
  if (!pointInPolygon(polygon, testPoint)) {
    bx = -bx;
    by = -by;
  }

  return { x: bx, y: by };
}

/**
 * @param {[number, number]} vertex
 * @param {[number, number]} prev
 * @param {[number, number]} next
 * @param {number} polygonOrientation
 * @returns {boolean}
 */
function isConvexVertex(vertex, prev, next, polygonOrientation) {
  if (!polygonOrientation) return false;
  const incomingX = vertex[0] - prev[0];
  const incomingY = vertex[1] - prev[1];
  const outgoingX = next[0] - vertex[0];
  const outgoingY = next[1] - vertex[1];
  const cross = incomingX * outgoingY - incomingY * outgoingX;
  return cross * polygonOrientation > 1e-8;
}

function buildInsetDistancesM(roomMinDimensionM) {
  const cornerInset = Math.min(0.65, Math.max(0.35, roomMinDimensionM * 0.08));
  return [
    cornerInset,
    Math.min(1.0, cornerInset + 0.15),
    Math.min(1.15, cornerInset + 0.3),
    Math.min(1.3, cornerInset + 0.45),
  ];
}

/**
 * @param {[number, number]} vertex
 * @param {[number, number]} prev
 * @param {[number, number]} next
 * @param {number[][]} polygon
 * @param {number} metersPerPixel
 * @param {number} marginM
 * @returns {[number, number] | null}
 */
function findCornerCameraPoint(vertex, prev, next, polygon, metersPerPixel, marginM, insetDistancesM) {
  const bisector = computeInteriorBisector(vertex, prev, next, polygon);
  let bestPoint = null;
  let bestClearance = -1;

  for (const angleDeg of ANGULAR_SAMPLES_DEG) {
    const angleRad = (angleDeg * Math.PI) / 180;
    const dir = rotate2d(bisector.x, bisector.y, angleRad);
    for (const insetM of insetDistancesM) {
      const insetPx = insetM / metersPerPixel;
      const candidate = [vertex[0] + dir.x * insetPx, vertex[1] + dir.y * insetPx];
      if (!isValidInteriorPolygonPoint(candidate, polygon, metersPerPixel, marginM)) continue;
      const clearance = distancePointToPolygonEdgeM(candidate, polygon, metersPerPixel);
      if (clearance > bestClearance) {
        bestClearance = clearance;
        bestPoint = candidate;
      }
    }
  }

  return bestPoint;
}

/**
 * @param {[number, number]} fromPolygonPoint
 * @param {[number, number]} interiorFocus
 * @param {{ x: number; y: number }} center
 * @param {number} metersPerPixel
 * @returns {{ x: number; y: number; z: number }}
 */
function buildTargetWorld(_fromPolygonPoint, interiorFocus, center, metersPerPixel) {
  const focusWorld = polygonPointToWorld(interiorFocus, center, metersPerPixel);
  return { x: focusWorld.x, y: TARGET_HEIGHT, z: focusWorld.z };
}

/**
 * @param {{ room?: { polygon?: number[][] } } | null} roomSelection
 * @param {number} metersPerPixel
 * @returns {Array<{ id: string; label: string; position: { x: number; y: number; z: number }; target: { x: number; y: number; z: number } }>}
 */
export function buildCameraPresets(roomSelection, metersPerPixel) {
  const polygon = roomSelection?.room?.polygon;
  if (!Array.isArray(polygon) || polygon.length < 3 || !Number.isFinite(metersPerPixel)) {
    return [];
  }

  const center = polygonCenter(polygon);
  const interiorFocus = findBestInteriorPoint(polygon, metersPerPixel, MIN_WALL_CLEARANCE, 24);
  if (!interiorFocus) return [];

  const presets = [];
  const vertexCount = polygon.length;
  const polygonOrientation = Math.sign(polygonSignedArea(polygon));
  const { minDimensionM } = polygonDimensionsM(polygon, metersPerPixel);
  const insetDistancesM = buildInsetDistancesM(minDimensionM);

  for (let i = 0; i < vertexCount; i += 1) {
    const prev = polygon[(i - 1 + vertexCount) % vertexCount];
    const vertex = polygon[i];
    const next = polygon[(i + 1) % vertexCount];
    if (!isConvexVertex(vertex, prev, next, polygonOrientation)) continue;

    const cornerPoint = findCornerCameraPoint(
      vertex,
      prev,
      next,
      polygon,
      metersPerPixel,
      CAMERA_WALL_MARGIN_M,
      insetDistancesM,
    );
    if (!cornerPoint) continue;

    const world = polygonPointToWorld(cornerPoint, center, metersPerPixel);
    const position = { x: world.x, y: CAMERA_HEIGHT, z: world.z };
    if (!isValidCameraWorldPosition(position, polygon, metersPerPixel, CAMERA_WALL_MARGIN_M)) continue;

    const target = buildTargetWorld(cornerPoint, interiorFocus, center, metersPerPixel);
    const targetPolygonPoint = worldToPolygonPoint(target.x, target.z, center, metersPerPixel);
    if (!pointInPolygon(polygon, targetPolygonPoint)) continue;

    presets.push({
      id: `view-${presets.length + 1}`,
      label: `View ${presets.length + 1}`,
      position,
      target,
      source: 'corner',
    });
  }

  if (presets.length === 0) {
    const world = polygonPointToWorld(interiorFocus, center, metersPerPixel);
    const position = { x: world.x, y: CAMERA_HEIGHT, z: world.z };
    if (isValidCameraWorldPosition(position, polygon, metersPerPixel, CAMERA_WALL_MARGIN_M)) {
      const focusWorld = polygonPointToWorld(interiorFocus, center, metersPerPixel);
      presets.push({
        id: 'view-1',
        label: 'Fallback View',
        position,
        target: { x: focusWorld.x, y: TARGET_HEIGHT, z: focusWorld.z },
        source: 'interior-fallback',
      });
    }
  }

  return presets;
}

/**
 * @param {{ x: number; y: number; z: number }} currentPos
 * @param {number} yaw
 * @param {'forward' | 'backward' | 'left' | 'right'} direction
 * @param {number} stepM
 * @param {number[][]} roomPolygon
 * @param {number} metersPerPixel
 * @param {number} [marginM]
 * @returns {{ x: number; y: number; z: number } | null}
 */
export function moveCameraInRoom(
  currentPos,
  yaw,
  direction,
  stepM,
  roomPolygon,
  metersPerPixel,
  marginM = CAMERA_WALL_MARGIN_M,
) {
  if (!currentPos || !Number.isFinite(yaw) || !Number.isFinite(stepM)) return null;

  let dx = 0;
  let dz = 0;
  switch (direction) {
    case 'forward':
      dx = Math.sin(yaw) * stepM;
      dz = Math.cos(yaw) * stepM;
      break;
    case 'backward':
      dx = -Math.sin(yaw) * stepM;
      dz = -Math.cos(yaw) * stepM;
      break;
    case 'left':
      dx = -Math.cos(yaw) * stepM;
      dz = Math.sin(yaw) * stepM;
      break;
    case 'right':
      dx = Math.cos(yaw) * stepM;
      dz = -Math.sin(yaw) * stepM;
      break;
    default:
      return null;
  }

  const candidate = {
    x: currentPos.x + dx,
    y: currentPos.y,
    z: currentPos.z + dz,
  };

  if (isValidCameraWorldPosition(candidate, roomPolygon, metersPerPixel, marginM)) {
    return candidate;
  }
  return null;
}

/**
 * @param {{ camera?: import('three').PerspectiveCamera; controls?: import('three/examples/jsm/controls/OrbitControls.js').OrbitControls }} sceneState
 * @param {{ x: number; y: number; active: boolean }} input
 * @param {number} deltaTime
 * @param {{ polygon: number[][]; metersPerPixel: number; marginM?: number }} bounds
 * @returns {boolean} whether movement was applied
 */
export function applyJoystickMovement(sceneState, input, deltaTime, bounds) {
  const camera = sceneState?.camera;
  const controls = sceneState?.controls;
  if (!camera || !controls || !input?.active) return false;
  if (!bounds?.polygon?.length || !Number.isFinite(bounds.metersPerPixel)) return false;
  if (!Number.isFinite(deltaTime) || deltaTime <= 0) return false;

  const magnitude = Math.hypot(input.x, input.y);
  if (magnitude <= JOYSTICK_DEAD_ZONE) return false;

  const forward = new THREE.Vector3();
  camera.getWorldDirection(forward);
  forward.y = 0;
  if (forward.lengthSq() <= 0.0001) return false;
  forward.normalize();

  const right = new THREE.Vector3().crossVectors(forward, camera.up).normalize();

  const movement = new THREE.Vector3();
  movement.addScaledVector(forward, -input.y);
  movement.addScaledVector(right, input.x);
  if (movement.lengthSq() <= 0) return false;

  movement.normalize().multiplyScalar(CAMERA_MOVE_SPEED * magnitude * deltaTime);

  const marginM = bounds.marginM ?? CAMERA_WALL_MARGIN_M;
  const currentY = camera.position.y;
  const currentDirection = new THREE.Vector3();
  camera.getWorldDirection(currentDirection);
  const targetDistance = Math.max(0.5, camera.position.distanceTo(controls.target) || 2);

  const tryApplyMovement = (delta) => {
    const candidate = {
      x: camera.position.x + delta.x,
      y: currentY,
      z: camera.position.z + delta.z,
    };
    if (!isValidCameraWorldPosition(candidate, bounds.polygon, bounds.metersPerPixel, marginM)) {
      return false;
    }
    camera.position.set(candidate.x, candidate.y, candidate.z);
    controls.target.copy(
      camera.position.clone().add(currentDirection.clone().multiplyScalar(targetDistance)),
    );
    camera.lookAt(controls.target);
    camera.updateMatrixWorld();
    if (sceneState?.lookOnlyState?.fixedPosition) {
      sceneState.lookOnlyState.fixedPosition.copy(camera.position);
    }
    controls.update();
    return true;
  };

  if (tryApplyMovement(movement)) return true;

  const xOnly = new THREE.Vector3(movement.x, 0, 0);
  if (xOnly.lengthSq() > 0 && tryApplyMovement(xOnly)) return true;

  const zOnly = new THREE.Vector3(0, 0, movement.z);
  if (zOnly.lengthSq() > 0 && tryApplyMovement(zOnly)) return true;

  return false;
}
